import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { AdminNotificationProvider, useAdminNotifications } from '../../context/AdminNotificationContext';
import AdminNotificationBell from '../../components/admin/AdminNotificationBell';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Settings,
  Wallet,
  LogOut,
  Menu,
  X,
  Shield,
  Tag,
  TrendingUp,
  Receipt,
  Plane,
  Building2,
  CreditCard,
  Megaphone,
  Banknote,
  Zap,
  LifeBuoy,
  Globe,
  Mail,
  MessageCircle,
  Activity,
  Server,
  Scale,
  BarChart3,
  PlaneTakeoff,
  Sparkles,
  Rocket,
  UserCog,
  ChevronDown,
  ChevronRight,
  Store,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EXPANDED_GROUPS_KEY = 'admin_sidebar_expanded_groups';

const TOP_LEVEL_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
];

const MENU_GROUPS = [
  {
    id: 'bookings',
    label: 'Bookings & Operations',
    items: [
      { icon: Plane, label: 'Flight Bookings', path: '/admin/bookings' },
      { icon: Building2, label: 'Hotel Bookings', path: '/admin/hotel-bookings' },
      { icon: Banknote, label: 'Refunds', path: '/admin/refunds' },
      { icon: Activity, label: 'Live Booking Monitor', path: '/admin/ops-monitor' },
      { icon: Zap, label: 'Ops Insights', path: '/admin/ops-insights' },
    ],
  },
  {
    id: 'people',
    label: 'People & Support',
    items: [
      { icon: Users, label: 'Customers', path: '/admin/users' },
      { icon: Briefcase, label: 'Travel Agents', path: '/admin/agents' },
      { icon: LifeBuoy, label: 'Support Tickets', path: '/admin/support' },
      { icon: UserCog, label: 'Admin Team', path: '/admin/staff' },
    ],
  },
  {
    id: 'counter_ops',
    label: 'Counter Ops',
    items: [
      { icon: Building2, label: 'Branches', path: '/admin/branches' },
      { icon: Store, label: 'Staff Users', path: '/admin/staff-users' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance & Pricing',
    items: [
      { icon: Wallet, label: 'Agent Wallets', path: '/admin/wallet' },
      { icon: CreditCard, label: 'Agent Credit', path: '/admin/credit' },
      { icon: Tag, label: 'Pricing & Markup', path: '/admin/markup' },
      { icon: Receipt, label: 'Tax & Compliance', path: '/admin/compliance' },
      { icon: TrendingUp, label: 'Reports', path: '/admin/reports' },
      { icon: Plane, label: 'TripJack Supplier', path: '/admin/supplier-tripjack' },
    ],
  },
  {
    id: 'catalog',
    label: 'Flight Catalog',
    items: [
      { icon: Plane, label: 'Offline Flights', path: '/admin/offline-inventory' },
      { icon: PlaneTakeoff, label: 'Airlines', path: '/admin/airlines' },
      { icon: Sparkles, label: 'Occasion Labels', path: '/admin/occasion-labels' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing & Communications',
    items: [
      { icon: Megaphone, label: 'Marketing', path: '/admin/marketing' },
      { icon: Mail, label: 'Email & SMS', path: '/admin/notifications' },
      { icon: MessageCircle, label: 'WhatsApp Bot', path: '/admin/whatsapp' },
      { icon: Globe, label: 'Website SEO', path: '/admin/seo' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    items: [
      { icon: BarChart3, label: 'Business Analytics', path: '/admin/analytics' },
    ],
  },
  {
    id: 'system',
    label: 'System & Settings',
    items: [
      { icon: Settings, label: 'General Settings', path: '/admin/settings' },
      { icon: Server, label: 'Supplier Integrations', path: '/admin/supplier-settings' },
      { icon: Rocket, label: 'Go-Live Checklist', path: '/admin/go-live-preflight' },
    ],
  },
];

const ALL_GROUP_IDS = MENU_GROUPS.map((g) => g.id);

function loadExpandedGroups() {
  try {
    const stored = localStorage.getItem(EXPANDED_GROUPS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((id) => ALL_GROUP_IDS.includes(id)));
      }
    }
  } catch {
    // ignore corrupt storage
  }
  return new Set(ALL_GROUP_IDS);
}

function saveExpandedGroups(expanded) {
  try {
    localStorage.setItem(EXPANDED_GROUPS_KEY, JSON.stringify([...expanded]));
  } catch {
    // ignore quota errors
  }
}

function NavLinkItem({ item, active, onNavigate, indented = true }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      className={`
        flex items-center justify-between gap-3 rounded-xl transition-all
        ${indented ? 'pl-8 pr-4 py-2.5' : 'px-4 py-3'}
        ${active
          ? 'bg-red-50 text-red-600 font-semibold shadow-sm'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }
      `}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Icon className={`w-5 h-5 shrink-0 ${active ? 'text-red-600' : ''}`} />
        <span className="truncate">{item.label}</span>
      </div>
      {item.badge && (
        <span className={`
          px-2 py-0.5 rounded-full text-xs font-semibold shrink-0
          ${active ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'}
        `}>
          {item.badge}
        </span>
      )}
    </Link>
  );
}

function AdminDashboardShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(loadExpandedGroups);
  const { byCategory } = useAdminNotifications();

  const badgesByPath = useMemo(() => ({
    '/admin/wallet': byCategory.wallet > 0 ? String(byCategory.wallet) : null,
    '/admin/support': byCategory.support > 0 ? String(byCategory.support) : null,
    '/admin/ops-monitor': (byCategory.monitor || 0) + (byCategory.booking || 0) > 0
      ? String((byCategory.monitor || 0) + (byCategory.booking || 0))
      : 'LIVE',
    '/admin/ops-insights': 'NEW',
    '/admin/go-live-preflight': 'GO',
  }), [byCategory]);

  const menuGroups = useMemo(() => MENU_GROUPS.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      badge: badgesByPath[item.path] ?? null,
    })),
  })), [badgesByPath]);

  const topLevelItems = useMemo(() => TOP_LEVEL_ITEMS.map((item) => ({
    ...item,
    badge: badgesByPath[item.path] ?? null,
  })), [badgesByPath]);

  const isActive = useCallback((path) => {
    if (path === '/admin/dashboard') {
      return location.pathname === '/admin/dashboard';
    }
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const findActiveGroupId = useCallback(() => {
    for (const group of menuGroups) {
      if (group.items.some((item) => isActive(item.path))) {
        return group.id;
      }
    }
    return null;
  }, [menuGroups, isActive]);

  useEffect(() => {
    const activeGroupId = findActiveGroupId();
    if (activeGroupId) {
      setExpandedGroups((prev) => {
        if (prev.has(activeGroupId)) return prev;
        const next = new Set(prev);
        next.add(activeGroupId);
        saveExpandedGroups(next);
        return next;
      });
    }
  }, [findActiveGroupId]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      saveExpandedGroups(next);
      return next;
    });
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, null, {
        withCredentials: true,
      });
    } catch (err) {
      console.error('Admin logout request failed:', err);
    } finally {
      localStorage.removeItem('admin_user');
      setLoggingOut(false);
      navigate('/admin/login', { replace: true });
    }
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-gray-900">Admin Panel</h1>
            <p className="text-xs text-gray-500">ACF Tourism</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <AdminNotificationBell />
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 bottom-0 w-72 bg-white border-r border-gray-200 z-40 transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-gray-900">Admin Panel</h1>
              <p className="text-xs text-gray-500">ACF Tourism</p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {topLevelItems.map((item) => (
            <NavLinkItem
              key={item.path}
              item={item}
              active={isActive(item.path)}
              onNavigate={closeSidebar}
              indented={false}
            />
          ))}

          <div className="pt-3 space-y-1">
            {menuGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.id);
              const Chevron = isExpanded ? ChevronDown : ChevronRight;

              return (
                <div key={group.id}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Chevron className="w-3.5 h-3.5 shrink-0" />
                    <span className="text-left">{group.label}</span>
                  </button>
                  {isExpanded && (
                    <div className="space-y-0.5">
                      {group.items.map((item) => (
                        <NavLinkItem
                          key={item.path}
                          item={item}
                          active={isActive(item.path)}
                          onNavigate={closeSidebar}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            data-testid="admin-logout-button"
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <LogOut className="w-5 h-5" />
            <span>{loggingOut ? 'Logging out…' : 'Logout'}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 pt-16 lg:pt-0">
        <div className="hidden lg:flex sticky top-0 z-30 items-center justify-between px-6 py-3 bg-white/95 backdrop-blur border-b border-gray-200">
          <p className="text-sm font-medium text-gray-600">Admin Panel</p>
          <AdminNotificationBell />
        </div>
        <Outlet />
      </main>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

const AdminDashboard = () => (
  <AdminNotificationProvider>
    <AdminDashboardShell />
  </AdminNotificationProvider>
);

export default AdminDashboard;
